/**
 * Source-gated content production manifest for Priority 20 Topics.
 *
 * This is planning metadata only. It intentionally does not contain article
 * bodies, source URLs, claim text, business data, expert data, solved counts,
 * engagement metrics, or D1 persistence rules.
 */

export type TopicContentRiskBand =
  | "R1-general"
  | "R2-regulated"
  | "R3-high-risk"
  | "R4-critical";

export type TopicContentJurisdiction =
  | "australia"
  | "nsw"
  | "state_required";

export type TopicContentScope =
  | "metadata_only"
  | "summary_faq_cta"
  | "full_guidance_target";

export type TopicContentStatus =
  | "planned"
  | "source_research"
  | "source_ready"
  | "draft_ko"
  | "editorial_review"
  | "specialist_review"
  | "approved_ko"
  | "english_summary"
  | "bilingual_review"
  | "published"
  | "review_soon"
  | "stale"
  | "archived";

export type TopicContentDisclaimerType =
  | "tax"
  | "legal"
  | "migration"
  | "medical"
  | "financial"
  | "general_high_risk";

export type TopicContentManifestEntry = {
  topicId: string;
  canonicalTopicId: string;
  riskBand: TopicContentRiskBand;
  jurisdictions: readonly TopicContentJurisdiction[];
  sourceRequired: boolean;
  specialistReviewRequired: boolean;
  reviewCycleDays: number;
  koreanScope: TopicContentScope;
  englishScope: TopicContentScope;
  stageIds: readonly string[];
  sourceOwner: string;
  contentStatus: TopicContentStatus;
  requiresDisclaimer: boolean;
  disclaimerType: TopicContentDisclaimerType;
};

const COMMON_STAGE_IDS = [
  "researching",
  "preparing",
  "applying",
  "stuck",
  "solved",
] as const;

const SOURCE_OWNER_UNASSIGNED = "unassigned";

export const CONTENT_TOPIC_CANONICAL_ALIASES = {
  "school-enrolment": "school",
} as const satisfies Record<string, string>;

export type ContentTopicAliasId = keyof typeof CONTENT_TOPIC_CANONICAL_ALIASES;

export const PRIORITY_TOPIC_CONTENT_MANIFEST = [
  {
    topicId: "abn",
    canonicalTopicId: "abn",
    riskBand: "R2-regulated",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "tax",
  },
  {
    topicId: "tfn",
    canonicalTopicId: "tfn",
    riskBand: "R2-regulated",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "applying", "stuck", "solved"],
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "tax",
  },
  {
    topicId: "renting",
    canonicalTopicId: "renting",
    riskBand: "R2-regulated",
    jurisdictions: ["nsw", "state_required"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["searching", "applying", "living", "moving-out"],
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "legal",
  },
  {
    topicId: "jobs",
    canonicalTopicId: "jobs",
    riskBand: "R1-general",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "preparing", "applying", "solved"],
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "general_high_risk",
  },
  {
    topicId: "nsw-driver-licence",
    canonicalTopicId: "nsw-driver-licence",
    riskBand: "R2-regulated",
    jurisdictions: ["nsw"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "general_high_risk",
  },
  {
    topicId: "gst",
    canonicalTopicId: "gst",
    riskBand: "R3-high-risk",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "preparing", "applying", "stuck"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "tax",
  },
  {
    topicId: "tax-return",
    canonicalTopicId: "tax-return",
    riskBand: "R3-high-risk",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "tax",
  },
  {
    topicId: "superannuation",
    canonicalTopicId: "superannuation",
    riskBand: "R3-high-risk",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 90,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "financial",
  },
  {
    topicId: "pay-and-wages",
    canonicalTopicId: "pay-and-wages",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "legal",
  },
  {
    topicId: "rental-bond-disputes",
    canonicalTopicId: "rental-bond-disputes",
    riskBand: "R3-high-risk",
    jurisdictions: ["nsw", "state_required"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["living", "moving-out", "stuck", "solved"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "legal",
  },
  {
    topicId: "medicare",
    canonicalTopicId: "medicare",
    riskBand: "R2-regulated",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 90,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "applying", "stuck", "solved"],
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "medical",
  },
  {
    topicId: "centrelink",
    canonicalTopicId: "centrelink",
    riskBand: "R3-high-risk",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "applying", "stuck", "solved"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "general_high_risk",
  },
  {
    topicId: "school",
    canonicalTopicId: "school",
    riskBand: "R2-regulated",
    jurisdictions: ["nsw", "state_required"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 90,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["researching", "preparing", "applying", "settled"],
    sourceOwner: "gpt_official_source_editor",
    contentStatus: "approved_ko",
    requiresDisclaimer: true,
    disclaimerType: "general_high_risk",
  },
  {
    topicId: "childcare",
    canonicalTopicId: "childcare",
    riskBand: "R2-regulated",
    jurisdictions: ["australia", "state_required"],
    sourceRequired: true,
    specialistReviewRequired: false,
    reviewCycleDays: 90,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "general_high_risk",
  },
  {
    topicId: "first-home-buyer",
    canonicalTopicId: "first-home-buyer",
    riskBand: "R3-high-risk",
    jurisdictions: ["nsw", "state_required"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 30,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["thinking", "loan-ready", "house-hunting", "contract", "solved"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "financial",
  },
  {
    topicId: "student-visa",
    canonicalTopicId: "student-visa",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["choosing-school", "preparing", "applying", "studying", "graduate-pathway"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "migration",
  },
  {
    topicId: "partner-visa",
    canonicalTopicId: "partner-visa",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "migration",
  },
  {
    topicId: "skilled-migration",
    canonicalTopicId: "skilled-migration",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "migration",
  },
  {
    topicId: "working-holiday",
    canonicalTopicId: "working-holiday",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: ["before-arrival", "first-3-months", "job-search", "regional-work", "solved"],
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "migration",
  },
  {
    topicId: "unfair-dismissal",
    canonicalTopicId: "unfair-dismissal",
    riskBand: "R4-critical",
    jurisdictions: ["australia"],
    sourceRequired: true,
    specialistReviewRequired: true,
    reviewCycleDays: 14,
    koreanScope: "full_guidance_target",
    englishScope: "summary_faq_cta",
    stageIds: COMMON_STAGE_IDS,
    sourceOwner: SOURCE_OWNER_UNASSIGNED,
    contentStatus: "planned",
    requiresDisclaimer: true,
    disclaimerType: "legal",
  },
] as const satisfies readonly TopicContentManifestEntry[];

export type PriorityTopicContentId =
  (typeof PRIORITY_TOPIC_CONTENT_MANIFEST)[number]["canonicalTopicId"];

export function resolveContentTopicId(topicId: string): string {
  return CONTENT_TOPIC_CANONICAL_ALIASES[
    topicId as ContentTopicAliasId
  ] ?? topicId;
}

export function getPriorityTopicContentEntry(
  topicId: string,
): TopicContentManifestEntry | undefined {
  const canonicalTopicId = resolveContentTopicId(topicId);
  return PRIORITY_TOPIC_CONTENT_MANIFEST.find(
    (entry) => entry.canonicalTopicId === canonicalTopicId,
  );
}

export function getPriorityTopicContentIds(): PriorityTopicContentId[] {
  return PRIORITY_TOPIC_CONTENT_MANIFEST.map(
    (entry) => entry.canonicalTopicId,
  );
}
